import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Plus, Save, Trash2, Edit3, X } from "lucide-react";

export default function AdminBoards() {
  const [boards, setBoards] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    const { data } = await api.get("/boards");
    setBoards(data);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6 fade-in" data-testid="admin-boards-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Boards</div>
          <h1 className="font-display text-3xl tracking-tight mt-2">Kanban Verwaltung</h1>
          <p className="text-white/60 text-sm mt-2">Boards anlegen, Spalten und Karten frei konfigurieren.</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary" data-testid="create-board-btn">
          <Plus className="w-4 h-4" /> Neues Board
        </button>
      </div>

      {error && <div className="text-red-400 text-sm font-mono">{error}</div>}

      {creating && (
        <BoardForm
          onCancel={() => setCreating(false)}
          onSave={async (data) => {
            try {
              await api.post("/boards", data);
              setCreating(false);
              load();
            } catch (e) {
              setError(formatApiError(e));
            }
          }}
        />
      )}

      <div className="space-y-3">
        {boards.map((b) => (
          editing === b.id ? (
            <BoardForm
              key={b.id}
              initial={b}
              onCancel={() => setEditing(null)}
              onSave={async (data) => {
                try {
                  await api.put(`/boards/${b.id}`, data);
                  setEditing(null);
                  load();
                } catch (e) {
                  setError(formatApiError(e));
                }
              }}
            />
          ) : (
            <BoardRow key={b.id} board={b} onEdit={() => setEditing(b.id)} onChanged={load} />
          )
        ))}
      </div>
    </div>
  );
}

function BoardForm({ initial, onSave, onCancel }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [wallpaperUrl, setWallpaperUrl] = useState(initial?.wallpaper_url || "");
  const [columns, setColumns] = useState(initial?.columns?.length ? initial.columns : [
    { id: "", title: "Anmeldungen", order: 0 },
    { id: "", title: "Aktiv", order: 1 },
    { id: "", title: "Abgeschlossen", order: 2 },
  ]);

  const addCol = () => setColumns([...columns, { id: "", title: "Neue Spalte", order: columns.length }]);
  const removeCol = (idx) => setColumns(columns.filter((_, i) => i !== idx));
  const setColTitle = (idx, t) => setColumns(columns.map((c, i) => i === idx ? { ...c, title: t } : c));

  return (
    <div className="card-solid p-5 space-y-4" data-testid="board-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="label">Titel</label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="board-title-input" />
        </div>
        <div>
          <label className="label">Wallpaper URL</label>
          <input className="input" value={wallpaperUrl} onChange={(e) => setWallpaperUrl(e.target.value)} placeholder="https://..." data-testid="board-wallpaper-input" />
        </div>
        <div className="md:col-span-2">
          <label className="label">Beschreibung</label>
          <textarea className="input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="board-description-input" />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="label !mb-0">Spalten</span>
          <button onClick={addCol} className="btn-ghost text-xs" data-testid="add-col-btn">
            <Plus className="w-3.5 h-3.5" /> Spalte
          </button>
        </div>
        <div className="space-y-2">
          {columns.map((c, i) => (
            <div key={i} className="flex gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 w-6 self-center">{String(i + 1).padStart(2, "0")}</span>
              <input className="input flex-1" value={c.title} onChange={(e) => setColTitle(i, e.target.value)} data-testid={`col-title-${i}`} />
              <button onClick={() => removeCol(i)} className="btn-ghost !p-2 text-red-400" data-testid={`remove-col-${i}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={() => onSave({ title, description, wallpaper_url: wallpaperUrl || null, columns })} className="btn-primary" data-testid="save-board-btn">
          <Save className="w-4 h-4" /> Speichern
        </button>
        <button onClick={onCancel} className="btn-secondary">Abbrechen</button>
      </div>
    </div>
  );
}

function BoardRow({ board, onEdit, onChanged }) {
  const [cards, setCards] = useState([]);
  const [showCards, setShowCards] = useState(false);
  const [adding, setAdding] = useState(false);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ column_id: "", title: "", content: "", image_url: "", color: "#29E9FF", member_id: "", stats: { wins: 0, losses: 0, points: 0 } });

  const loadCards = async () => {
    const { data } = await api.get(`/boards/${board.id}`);
    setCards(data.cards || []);
  };

  useEffect(() => {
    if (showCards) loadCards();
    api.get("/users").then((r) => setUsers(r.data));
  }, [showCards]);  // eslint-disable-line

  const remove = async () => {
    if (!window.confirm("Board und alle Karten löschen?")) return;
    await api.delete(`/boards/${board.id}`);
    onChanged?.();
  };

  const addCard = async () => {
    const target = users.find((u) => u.user_id === form.member_id);
    const payload = {
      column_id: form.column_id,
      title: form.title || target?.name || "Karte",
      content: form.content,
      image_url: form.image_url || target?.picture || null,
      color: form.color,
      member_id: form.member_id || null,
      stats: form.stats,
    };
    await api.post(`/boards/${board.id}/cards`, payload);
    setForm({ ...form, title: "", content: "" });
    setAdding(false);
    loadCards();
  };

  const removeCard = async (cid) => {
    if (!window.confirm("Karte löschen?")) return;
    await api.delete(`/boards/${board.id}/cards/${cid}`);
    loadCards();
  };

  return (
    <div className="card-solid p-4">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg tracking-tight">{board.title}</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-0.5">
            {board.columns?.length || 0} Spalten
          </div>
        </div>
        <button onClick={() => setShowCards((s) => !s)} className="btn-ghost text-xs" data-testid={`toggle-cards-${board.id}`}>
          {showCards ? "Schließen" : "Karten verwalten"}
        </button>
        <button onClick={onEdit} className="btn-ghost !p-2" data-testid={`edit-board-${board.id}`}><Edit3 className="w-4 h-4" /></button>
        <button onClick={remove} className="btn-ghost !p-2 text-red-400" data-testid={`delete-board-${board.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>

      {showCards && (
        <div className="mt-4 space-y-3">
          <button onClick={() => { setForm({ ...form, column_id: board.columns[0]?.id || "" }); setAdding(true); }} className="btn-secondary text-xs" data-testid={`add-card-toggle-${board.id}`}>
            <Plus className="w-3.5 h-3.5" /> Karte hinzufügen
          </button>

          {adding && (
            <div className="card-solid p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="label">Spalte</label>
                  <select className="input" value={form.column_id} onChange={(e) => setForm({ ...form, column_id: e.target.value })}>
                    {board.columns.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Mitglied (optional)</label>
                  <select className="input" value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })}>
                    <option value="">— frei —</option>
                    {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Titel</label>
                  <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div>
                  <label className="label">Akzent-Farbe</label>
                  <input className="input" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Inhalt / Notiz</label>
                  <textarea className="input" rows={2} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
                </div>
                {["wins","losses","points"].map((k) => (
                  <div key={k}>
                    <label className="label">{k}</label>
                    <input type="number" className="input" value={form.stats[k]} onChange={(e) => setForm({ ...form, stats: { ...form.stats, [k]: parseInt(e.target.value || "0", 10) } })} />
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={addCard} className="btn-primary"><Save className="w-4 h-4" /> Speichern</button>
                <button onClick={() => setAdding(false)} className="btn-secondary">Abbrechen</button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {cards.map((c) => {
              const col = board.columns.find((x) => x.id === c.column_id);
              return (
                <div key={c.id} className="border border-white/10 p-3 flex items-center gap-3" data-testid={`admin-card-${c.id}`}>
                  <div className="w-1 h-10 rounded-sm" style={{ background: c.color || "#29E9FF" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{c.title}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{col?.title || "—"}</div>
                  </div>
                  <button onClick={() => removeCard(c.id)} className="btn-ghost !p-2 text-red-400">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
